import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  ButtonGroup,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField
} from '@mui/material';
import { Canvas } from '@react-three/fiber';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import SearchIcon from '@mui/icons-material/Search';
import { API_BASE } from '../config';
import Scene from './MemoryGraph3D';

const MemoryGraphViewer = () => {
  const [nodes, setNodes] = useState([]);
  const [allNodes, setAllNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchMemoryGraph = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/memory/graph?limit=200`);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setNodes([]);
        setAllNodes([]);
      } else {
        // Map backend coordinates to render coordinates and add defaults
        const processedNodes = (data.nodes || []).map(node => ({
          ...node,
          renderX: (node.x || 0) * 10, // Scale up for visibility
          renderY: (node.y || 0) * 10,
          renderZ: (node.z || 0) * 10,
          radius: 0.15,
          color: getNodeColor(node.type),
          opacity: 1,
          msg: node.msg || node.content || '',
          snippet: (node.msg || node.content || '').slice(0, 100)
        }));
        
        setAllNodes(processedNodes);
        setNodes(processedNodes);
      }
    } catch (err) {
      setError('Failed to load memory graph');
      setNodes([]);
      setAllNodes([]);
    } finally {
      setLoading(false);
    }
  };

  const getNodeColor = (type) => {
    const colors = {
      prompt: '#3ad0de',
      result: '#7595ff',
      trace: '#D183C9',
      unknown: '#b0b4c4'
    };
    return colors[type] || colors.unknown;
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/memory/stats`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      // Failed to load stats
    }
  };

  const handleFilterType = (type) => {
    setFilterType(type);
    applyFilters(type, searchQuery);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(filterType, query);
  };

  const applyFilters = (type, query) => {
    let filtered = allNodes;

    // Apply type filter
    if (type !== 'all') {
      filtered = filtered.filter(node => node.type === type);
    }

    // Apply search filter
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(node => 
        (node.msg || '').toLowerCase().includes(lowerQuery) ||
        (node.type || '').toLowerCase().includes(lowerQuery)
      );
    }

    setNodes(filtered);
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  useEffect(() => {
    fetchMemoryGraph();
    fetchStats();
  }, []);

  const countsByType = allNodes.reduce((acc, node) => {
    acc[node.type] = (acc[node.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ 
      height: 'calc(100vh - 180px)', 
      m: 3, 
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)',
      overflow: 'hidden'
    }}>
      {/* Header with Controls - ONE ROW */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        background: 'rgba(0, 0, 0, 0.2)'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#3ad0de', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewInArIcon sx={{ fontSize: 28 }} />
          Memory Graph
        </Typography>

        <TextField
          size="small"
          placeholder="Search nodes..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
          }}
        />

        <ButtonGroup size="small">
          <Button
            variant={filterType === 'all' ? 'contained' : 'outlined'}
            onClick={() => handleFilterType('all')}
          >
            All ({allNodes.length})
          </Button>
          <Button
            variant={filterType === 'prompt' ? 'contained' : 'outlined'}
            onClick={() => handleFilterType('prompt')}
            sx={{ borderColor: '#3ad0de', color: filterType === 'prompt' ? 'white' : '#3ad0de' }}
          >
            Prompts ({countsByType.prompt || 0})
          </Button>
          <Button
            variant={filterType === 'result' ? 'contained' : 'outlined'}
            onClick={() => handleFilterType('result')}
            sx={{ borderColor: '#7595ff', color: filterType === 'result' ? 'white' : '#7595ff' }}
          >
            Results ({countsByType.result || 0})
          </Button>
          <Button
            variant={filterType === 'trace' ? 'contained' : 'outlined'}
            onClick={() => handleFilterType('trace')}
            sx={{ borderColor: '#D183C9', color: filterType === 'trace' ? 'white' : '#D183C9' }}
          >
            Traces ({countsByType.trace || 0})
          </Button>
        </ButtonGroup>

        <Box sx={{ flex: 1 }} />

        <Chip
          label={`${nodes.length} nodes`}
          size="small"
          sx={{ background: '#3ad0de', color: 'white', fontWeight: 600 }}
        />

        <Tooltip title="Refresh graph">
          <IconButton
            onClick={fetchMemoryGraph}
            disabled={loading}
            size="small"
            sx={{ color: '#3ad0de' }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 3D Graph Canvas - Takes remaining height */}
      <Box sx={{ 
  flex: 1, 
  position: 'relative',
  background: 'none'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
            <CircularProgress size={60} sx={{ color: '#3ad0de' }} />
            <Typography color="text.secondary">Loading memory graph...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
            <Alert severity="error" sx={{ maxWidth: 600 }}>{error}</Alert>
          </Box>
        ) : nodes.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
            <Alert severity="info" sx={{ maxWidth: 600 }}>
              No memory nodes found. Start a workflow to populate the graph!
            </Alert>
          </Box>
        ) : (
          <Canvas 
            camera={{ position: [0, 0, 15], fov: 60 }}
            gl={{ 
              alpha: true, 
              antialias: true,
              premultipliedAlpha: false
            }}
            style={{ background: 'transparent', width: '100%', height: '100%' }}
          >
            <Scene nodes={nodes} onNodeClick={handleNodeClick} selectedNode={selectedNode} />
          </Canvas>
        )}

        {/* Selected Node Details - Overlay */}
        {selectedNode && (
          <Paper sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            p: 2.5,
            maxWidth: 400,
            maxHeight: 500,
            overflow: 'auto',
            background: 'rgba(0, 0, 0, 0.92)',
            backdropFilter: 'blur(15px)',
            border: '2px solid #3ad0de',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(58, 208, 222, 0.3)'
          }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600} color="#3ad0de" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ViewInArIcon /> Node Details
            </Typography>
            <Divider sx={{ mb: 2, borderColor: 'rgba(58, 208, 222, 0.3)' }} />
            
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Type" 
                  secondary={selectedNode.type || 'unknown'}
                  primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                  secondaryTypographyProps={{ variant: 'body2', color: '#e5e7eb' }}
                />
              </ListItem>
              
              {selectedNode.agent && (
                <ListItem>
                  <ListItemText 
                    primary="Agent" 
                    secondary={selectedNode.agent}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                    secondaryTypographyProps={{ variant: 'body2', color: '#e5e7eb' }}
                  />
                </ListItem>
              )}
              
              {selectedNode.model && (
                <ListItem>
                  <ListItemText 
                    primary="Model" 
                    secondary={selectedNode.model}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                    secondaryTypographyProps={{ variant: 'body2', color: '#e5e7eb' }}
                  />
                </ListItem>
              )}
              
              <ListItem>
                <ListItemText 
                  primary="Content" 
                  secondary={selectedNode.msg?.slice(0, 300) || 'No content'}
                  primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                  secondaryTypographyProps={{ variant: 'body2', color: '#e5e7eb', sx: { whiteSpace: 'pre-wrap' } }}
                />
              </ListItem>
              
              {selectedNode.prompt && (
                <ListItem>
                  <ListItemText 
                    primary="Original Prompt" 
                    secondary={selectedNode.prompt}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                    secondaryTypographyProps={{ variant: 'caption', color: '#9ca3af', fontStyle: 'italic' }}
                  />
                </ListItem>
              )}
              
              {selectedNode.tools_used && selectedNode.tools_used.length > 0 && (
                <ListItem>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" fontWeight={600} color="#9ca3af">Tools Used</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {selectedNode.tools_used.map((tool, i) => (
                        <Chip 
                          key={i} 
                          label={tool} 
                          size="small" 
                          sx={{ 
                            background: 'rgba(58, 208, 222, 0.2)', 
                            color: '#3ad0de', 
                            fontSize: '0.7rem',
                            height: 20,
                            border: '1px solid rgba(58, 208, 222, 0.4)'
                          }} 
                        />
                      ))}
                    </Box>
                  </Box>
                </ListItem>
              )}
              
              {(selectedNode.execution_time || selectedNode.cost || selectedNode.tokens) && (
                <ListItem>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" fontWeight={600} color="#9ca3af" sx={{ mb: 0.5, display: 'block' }}>Metrics</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedNode.execution_time && (
                        <Chip label={`⏱️ ${selectedNode.execution_time}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      )}
                      {selectedNode.cost && (
                        <Chip label={`💰 ${selectedNode.cost}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      )}
                      {selectedNode.tokens && (
                        <Chip label={`🔢 ${selectedNode.tokens}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      )}
                    </Box>
                  </Box>
                </ListItem>
              )}
              
              {selectedNode.timestamp && (
                <ListItem>
                  <ListItemText 
                    primary="Timestamp" 
                    secondary={new Date(selectedNode.timestamp).toLocaleString()}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                    secondaryTypographyProps={{ variant: 'caption', color: '#9ca3af' }}
                  />
                </ListItem>
              )}
              
              {selectedNode.run_id && (
                <ListItem>
                  <ListItemText 
                    primary="Run ID" 
                    secondary={selectedNode.run_id}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: '#9ca3af' }}
                    secondaryTypographyProps={{ variant: 'caption', color: '#6b7280', fontFamily: 'monospace' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </Box>

      {/* Stats Footer */}
      {stats && (
        <Box sx={{ 
          p: 1.5, 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.2)',
          flexShrink: 0
        }}>
          <Chip label={`Total: ${stats.total_nodes || 0}`} size="small" variant="outlined" />
          <Chip label={`Prompts: ${stats.by_type?.prompt || 0}`} size="small" sx={{ background: '#3ad0de20', borderColor: '#3ad0de', color: '#3ad0de' }} />
          <Chip label={`Results: ${stats.by_type?.result || 0}`} size="small" sx={{ background: '#7595ff20', borderColor: '#7595ff', color: '#7595ff' }} />
          <Chip label={`Traces: ${stats.by_type?.trace || 0}`} size="small" sx={{ background: '#D183C920', borderColor: '#D183C9', color: '#D183C9' }} />
        </Box>
      )}
    </Box>
  );
};

export default MemoryGraphViewer;
